import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { VenueId } from '@/api/types';
import { AmbienceEngine, DEFAULT_VOLUME, type AmbienceDeps } from './ambience';
import { venueOf } from './catalog';

interface AmbiencePrefs {
  muted: boolean;
  volume: number;
  setMuted: (m: boolean) => void;
  setVolume: (v: number) => void;
}

const safeStorage = createJSONStorage(() => {
  try {
    const s = window.localStorage;
    s.setItem('__popin_probe__', '1');
    s.removeItem('__popin_probe__');
    return s;
  } catch {
    const mem = new Map<string, string>();
    return { getItem: (k: string) => mem.get(k) ?? null, setItem: (k: string, v: string) => void mem.set(k, v), removeItem: (k: string) => void mem.delete(k) };
  }
});

/** The v9–v13 default volume: a device still on it moves to the new default. */
const OLD_DEFAULT_VOLUME = 0.45;

/** Per-device convenience: the ambience's mute and volume (never synced). */
export const useAmbiencePrefs = create<AmbiencePrefs>()(
  persist(
    (set) => ({
      muted: false,
      volume: DEFAULT_VOLUME,
      setMuted: (muted) => set({ muted }),
      setVolume: (volume) => set({ volume: Math.min(1, Math.max(0, volume)), muted: volume <= 0 }),
    }),
    {
      name: 'popin.ambience',
      storage: safeStorage,
      version: 1,
      // v14: the default dropped to 30 %; a volume someone chose stays.
      migrate: (old, version) => {
        const s = (old ?? {}) as Partial<AmbiencePrefs>;
        if (version < 1 && s.volume === OLD_DEFAULT_VOLUME) s.volume = DEFAULT_VOLUME;
        return s as AmbiencePrefs;
      },
    },
  ),
);

type Consent = 'on' | 'off';

interface ThemeConsent {
  byDate: Record<string, Consent>;
  set: (dateId: string, c: Consent) => void;
}

/**
 * v14: a date's theme (frame + sound) starts only once you say so — asked
 * once per date on this device. Kept for the tab's life (a reload mid-date
 * doesn't ask again).
 */
export const useThemeConsent = create<ThemeConsent>()(
  persist(
    (set) => ({
      byDate: {},
      set: (dateId, c) => set((s) => ({ byDate: { ...s.byDate, [dateId]: c } })),
    }),
    {
      name: 'popin.theme-consent',
      storage: createJSONStorage(() => {
        try {
          return window.sessionStorage;
        } catch {
          const mem = new Map<string, string>();
          return { getItem: (k: string) => mem.get(k) ?? null, setItem: (k: string, v: string) => void mem.set(k, v), removeItem: (k: string) => void mem.delete(k) };
        }
      }),
    },
  ),
);

/**
 * Plays the venue's ambience while `enabled`, at the viewer's volume, ducked
 * while `speaking` (own mic). Unmounting, disabling or muting tears the audio
 * graph down completely.
 */
export function useVenueAmbience(venue: VenueId | null | undefined, opts: { enabled: boolean; speaking: boolean; deps?: AmbienceDeps }) {
  const { muted, volume } = useAmbiencePrefs();
  const engine = useRef<AmbienceEngine | null>(null);
  if (!engine.current) engine.current = new AmbienceEngine(opts.deps);
  const recipe = opts.enabled ? (venueOf(venue)?.ambience ?? null) : null;

  useEffect(() => () => engine.current?.dispose(), []);
  useEffect(() => {
    engine.current!.setVolume(volume);
    engine.current!.setMuted(muted);
  }, [muted, volume]);
  useEffect(() => engine.current!.setRecipe(recipe), [recipe]);
  useEffect(() => engine.current!.duck(opts.speaking), [opts.speaking]);
  return engine.current;
}
